import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Search, X, Loader2, ArrowUp, ArrowDown, CornerDownLeft } from "lucide-react";
import useBarcodeScanner from "@charphq/ui/hooks/useBarcodeScanner";
import { useSearchShortcut } from "@charphq/ui/hooks/useSearchShortcut";
import { useUniversalSearch } from "@charphq/ui/hooks/useUniversalSearch";
import { useUniversalSearchContext } from "@charphq/ui/providers/universal-search-provider";
import { DeviceCard } from "./entity-cards/DeviceCard";
import { SimCardCard } from "./entity-cards/SimCardCard";
import { CustomerCard } from "./entity-cards/CustomerCard";
import { ProjectCard } from "./entity-cards/ProjectCard";
import { DeploymentCard } from "./entity-cards/DeploymentCard";
import { entityTypeMeta, type EntityKind } from "./entity-cards/entityTypeMeta";
import { Input } from "../base/input";
import { Button } from "../base/button";
import { cn } from "@charphq/ui/lib/utils";
import { simCardsSearchForIccid } from "../../utils/simCardLinks";

/** Labeled header that introduces a group of same-typed results. */
function SectionHeader({ kind, count }: { kind: EntityKind; count: number }) {
  const { label, Icon } = entityTypeMeta[kind];
  return (
    <div className="flex items-center gap-1.5 px-1 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3 w-3" />
      <span>{label}s</span>
      <span className="text-muted-foreground/60">({count})</span>
    </div>
  );
}

export function UniversalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { isOpen, close, open } = useSearchShortcut();
  const { searchState, setQuery, clearSearch, hasResults, totalResults, refetchDevices } =
    useUniversalSearch();
  const { isEnabled, customHandler, deviceActionRenderer, setCloseSearch } =
    useUniversalSearchContext();

  const [isVisible, setIsVisible] = useState(false);
  const [searchSessionId, setSearchSessionId] = useState<string>("");
  const [hasAttemptedImport, setHasAttemptedImport] = useState(false);
  // -1 means "focus on the input, no result selected". ArrowDown moves into the
  // results; ArrowUp from the top returns here.
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Register close function in context so external components can close the search
  useEffect(() => {
    setCloseSearch(close);
    return () => setCloseSearch(null);
  }, [close, setCloseSearch]);

  // Barcode scanner integration
  // Enable scanner even when popup is closed, so barcode scans can open it
  useBarcodeScanner({
    prefix: "~",
    suffix1: "~",
    suffix2: "~",
    enabled: isEnabled, // Remove && isOpen so scanner works when popup is closed
    transform: (barcode: string) => {
      // Apply existing transform logic
      if (barcode.startsWith("https://go.charp.be/")) {
        barcode = barcode.slice(20);
      }
      return barcode;
    },
    onScan: (barcode: string) => {
      if (customHandler) {
        customHandler(barcode);
      } else {
        // Open popup if it's not already open
        if (!isOpen) {
          open();
        }

        // Set the query (this will trigger search)
        setQuery(barcode);

        // Generate new session ID for barcode scan
        if (barcode.length >= 2) {
          setSearchSessionId(`${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
        }
      }
    },
  });

  // Handle visibility animation; reset the input on close so every reopen starts
  // empty. Clearing on close (not open) leaves the barcode-scan path — which calls
  // open() and then setQuery(barcode) — untouched.
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Focus input after animation
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setIsVisible(false);
      setQuery("");
    }
  }, [isOpen, setQuery]);

  // Handle import attempt when no results found
  useEffect(() => {
    // Reset import attempt flag and keyboard selection when query changes
    setHasAttemptedImport(false);
    setSelectedIndex(-1);
  }, [searchState.query]);

  // Refetch after sync attempt when no results found
  useEffect(() => {
    const shouldAttemptImport =
      !hasResults &&
      !searchState.devices.isLoading &&
      !searchState.simCards.isLoading &&
      searchState.query.length >= 2 &&
      !hasAttemptedImport;

    if (shouldAttemptImport) {
      // The sync parameter is already being passed in the hook, which triggers backend import
      // Wait a bit for the import to complete, then refetch
      setHasAttemptedImport(true);
      const timeoutId = setTimeout(async () => {
        try {
          await refetchDevices();
        } catch (error) {
          console.error("Failed to refetch devices after import attempt:", error);
        }
      }, 1000); // Wait 1 second for backend import to complete

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [
    hasResults,
    searchState.devices.isLoading,
    searchState.simCards.isLoading,
    searchState.query,
    hasAttemptedImport,
    refetchDevices,
  ]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
    return undefined;
  }, [isOpen, close]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest("[data-universal-search]")) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, close]);

  const handleDeviceClick = (deviceId: string) => {
    navigate({ to: `/devices/${deviceId}` });
    close();
  };

  const handleCustomerClick = (customerId: string) => {
    navigate({ to: `/customers/${customerId}` });
    close();
  };

  const handleProjectClick = (projectId: string) => {
    navigate({ to: `/projects/${projectId}` });
    close();
  };

  const handleDeploymentClick = (deploymentId: string) => {
    navigate({ to: `/deployments/${deploymentId}` });
    close();
  };

  // There is no /sim-cards/:id detail page, so deep-link into the filtered list
  // instead. This used to navigate to a route that does not exist — clicking a
  // SIM result simply did nothing (OTO-157).
  const handleSimCardClick = (iccid: string) => {
    navigate({ to: "/sim-cards", search: simCardsSearchForIccid(iccid) });
    close();
  };

  // Flat, ordered list of navigable results. The order here is the SINGLE source
  // of truth for both keyboard navigation and the rendered section order below —
  // an index into this list must map to the same card on screen. Keep the two in
  // lockstep: devices, customers, projects, deployments, SIM cards. Computed each
  // render (cheap; nothing depends on its referential identity).
  const navItems = [
    ...searchState.devices.data.map((device) => ({
      id: device.id,
      open: () => handleDeviceClick(device.id),
    })),
    ...searchState.customers.data.map((customer) => ({
      id: customer.id,
      open: () => handleCustomerClick(customer.id),
    })),
    ...searchState.projects.data.map((project) => ({
      id: project.id,
      open: () => handleProjectClick(project.id),
    })),
    ...searchState.deployments.data.map((deployment) => ({
      id: deployment.id,
      open: () => handleDeploymentClick(deployment.id),
    })),
    ...searchState.simCards.data.map((simCard) => ({
      id: simCard.id,
      open: () => handleSimCardClick(simCard.iccid),
    })),
  ];

  // Per-section base offsets into navItems, matching the order above. Each
  // section's card at local index `i` has global nav index `<offset> + i`.
  const devicesOffset = 0;
  const customersOffset = devicesOffset + searchState.devices.data.length;
  const projectsOffset = customersOffset + searchState.customers.data.length;
  const deploymentsOffset = projectsOffset + searchState.projects.data.length;
  const simCardsOffset = deploymentsOffset + searchState.deployments.data.length;

  const isAnyLoading =
    searchState.devices.isLoading ||
    searchState.customers.isLoading ||
    searchState.projects.isLoading ||
    searchState.deployments.isLoading ||
    searchState.simCards.isLoading;
  const isAnyError = Boolean(
    searchState.devices.error ||
    searchState.customers.error ||
    searchState.projects.error ||
    searchState.deployments.error ||
    searchState.simCards.error,
  );

  // Clamp a stale index (e.g. results shrank) back to "no selection".
  const activeIndex = selectedIndex < navItems.length ? selectedIndex : -1;

  // Refs to each rendered card, keyed by nav index, for scroll-into-view.
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Keep the highlighted card visible within the scrollable results area.
  useEffect(() => {
    if (activeIndex < 0) return;
    itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      if (navItems.length === 0) return;
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, navItems.length - 1));
    } else if (e.key === "ArrowUp") {
      if (navItems.length === 0) return;
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      if (activeIndex < 0) return;
      e.preventDefault();
      navItems[activeIndex]?.open();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    // Generate new session ID when query changes (new search)
    if (newQuery.length >= 2) {
      setSearchSessionId(`${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    }
  };

  const handleClear = () => {
    clearSearch();
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-start justify-center pt-[10vh]",
        "bg-black/50 backdrop-blur-sm transition-opacity duration-200",
        isVisible ? "opacity-100" : "opacity-0",
      )}
    >
      <div
        data-universal-search
        className={cn(
          "w-full max-w-2xl mx-4 rounded-lg border bg-background shadow-lg",
          "transition-all duration-200",
          isVisible ? "translate-y-0 scale-100 opacity-100" : "-translate-y-4 scale-95 opacity-0",
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b p-4">
          <Search className="h-5 w-5 text-muted-foreground" />
          <Input
            ref={inputRef}
            placeholder="Search devices, customers, projects, deployments, SIM cards, or scan barcode..."
            value={searchState.query}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            className="flex-1 border-0 bg-transparent p-0 text-base shadow-none focus-visible:ring-0"
          />
          {searchState.query && (
            <Button variant="ghost" size="sm" onClick={handleClear} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto p-4">
          {searchState.query.length < 2 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="mx-auto h-8 w-8 mb-2 opacity-50" />
              <p>Type at least 2 characters to search</p>
              <p className="text-sm mt-1">Or scan a barcode with ~ prefix and ~~ suffix</p>
            </div>
          ) : (
            <div>
              {/* Loading indicator */}
              {isAnyLoading && (
                <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Searching...</span>
                </div>
              )}

              {/* Error indicator */}
              {isAnyError && (
                <div className="mb-4">
                  <span className="text-sm text-destructive">Error loading some results</span>
                </div>
              )}

              {/* Grouped results — one labeled section per entity type. Section
                  order MUST match the navItems order so keyboard nav lines up. */}
              {hasResults ? (
                <div className="space-y-4">
                  {/* Devices */}
                  {searchState.devices.data.length > 0 && (
                    <div>
                      <SectionHeader kind="device" count={searchState.devices.data.length} />
                      <div className="space-y-2">
                        {searchState.devices.data.map((device, index) => {
                          const navIndex = devicesOffset + index;
                          return (
                            <div
                              key={`device-${device.id}`}
                              ref={(el) => {
                                itemRefs.current[navIndex] = el;
                              }}
                            >
                              <DeviceCard
                                device={device}
                                onClick={() => handleDeviceClick(device.id)}
                                isSelected={activeIndex === navIndex}
                                autoSync={totalResults === 1}
                                searchSessionId={searchSessionId}
                                renderActions={deviceActionRenderer ?? undefined}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Customers */}
                  {searchState.customers.data.length > 0 && (
                    <div>
                      <SectionHeader kind="customer" count={searchState.customers.data.length} />
                      <div className="space-y-2">
                        {searchState.customers.data.map((customer, index) => {
                          const navIndex = customersOffset + index;
                          return (
                            <div
                              key={`customer-${customer.id}`}
                              ref={(el) => {
                                itemRefs.current[navIndex] = el;
                              }}
                            >
                              <CustomerCard
                                customer={customer}
                                onClick={() => handleCustomerClick(customer.id)}
                                isSelected={activeIndex === navIndex}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Projects */}
                  {searchState.projects.data.length > 0 && (
                    <div>
                      <SectionHeader kind="project" count={searchState.projects.data.length} />
                      <div className="space-y-2">
                        {searchState.projects.data.map((project, index) => {
                          const navIndex = projectsOffset + index;
                          return (
                            <div
                              key={`project-${project.id}`}
                              ref={(el) => {
                                itemRefs.current[navIndex] = el;
                              }}
                            >
                              <ProjectCard
                                project={project}
                                onClick={() => handleProjectClick(project.id)}
                                isSelected={activeIndex === navIndex}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Deployments */}
                  {searchState.deployments.data.length > 0 && (
                    <div>
                      <SectionHeader
                        kind="deployment"
                        count={searchState.deployments.data.length}
                      />
                      <div className="space-y-2">
                        {searchState.deployments.data.map((deployment, index) => {
                          const navIndex = deploymentsOffset + index;
                          return (
                            <div
                              key={`deployment-${deployment.id}`}
                              ref={(el) => {
                                itemRefs.current[navIndex] = el;
                              }}
                            >
                              <DeploymentCard
                                deployment={deployment}
                                onClick={() => handleDeploymentClick(deployment.id)}
                                isSelected={activeIndex === navIndex}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* SIM Cards */}
                  {searchState.simCards.data.length > 0 && (
                    <div>
                      <SectionHeader kind="simcard" count={searchState.simCards.data.length} />
                      <div className="space-y-2">
                        {searchState.simCards.data.map((simCard, index) => {
                          const navIndex = simCardsOffset + index;
                          return (
                            <div
                              key={`simcard-${simCard.id}`}
                              ref={(el) => {
                                itemRefs.current[navIndex] = el;
                              }}
                            >
                              <SimCardCard
                                simCard={simCard}
                                onClick={() => handleSimCardClick(simCard.iccid)}
                                isSelected={activeIndex === navIndex}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                !isAnyLoading && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="mx-auto h-8 w-8 mb-2 opacity-50" />
                    <p>No results found for "{searchState.query}"</p>
                    {searchState.query.length >= 2 && !hasAttemptedImport && (
                      <p className="text-xs mt-2">Searching external systems...</p>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {searchState.query.length >= 2 && (
          <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
            <span>
              {hasResults ? `${totalResults} result${totalResults !== 1 ? "s" : ""} found` : ""}
            </span>
            {hasResults && (
              <span className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="inline-flex items-center rounded border bg-muted px-1 py-0.5">
                    <ArrowUp className="h-3 w-3" />
                  </kbd>
                  <kbd className="inline-flex items-center rounded border bg-muted px-1 py-0.5">
                    <ArrowDown className="h-3 w-3" />
                  </kbd>
                  to navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="inline-flex items-center rounded border bg-muted px-1 py-0.5">
                    <CornerDownLeft className="h-3 w-3" />
                  </kbd>
                  to open
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
